import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../../config/api";
import "./index.css";

const PROVIDER_NAMES = { stripe: "Stripe", paypal: "PayPal", poynt: "GoDaddy / Poynt" };

export default function PaymentsPage() {
  const { guid } = useParams();
  const navigate = useNavigate();
  const collect = useRef(null);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState("");
  const [working, setWorking] = useState(false);

  useEffect(() => {
    apiFetch(`/api/payment-processing/checkout/${guid}/`)
      .then(({ data }) => setDetails(data))
      .catch((e) => setError(e.response?.data?.error || "Unable to load payment details."));
  }, [guid]);

  const start = async (nonce) => {
    setWorking(true);
    setError("");
    try {
      const { data } = await apiFetch(`/api/payment-processing/checkout/${guid}/start/`, {
        method: "POST",
        body: nonce ? { nonce } : {},
      });
      if (data.checkout_url) window.location.assign(data.checkout_url);
      else navigate(`/payment-success?guid=${guid}&reference=${encodeURIComponent(data.reference)}`);
    } catch (e) {
      setError(e.response?.data?.error || "Unable to start payment.");
      setWorking(false);
    }
  };

  useEffect(() => {
    if (details?.processor !== "poynt") return undefined;
    let active = true;
    const initialize = () => {
      if (!active || !window.TokenizeJs) {
        if (active) setError("The secure Poynt card form could not be loaded.");
        return;
      }
      const instance = new window.TokenizeJs(
        details.client_config.business_id,
        details.client_config.application_id
      );
      instance.on("nonce", (event) => {
        const nonce = event?.nonce || event?.data?.nonce;
        if (nonce) start(nonce);
        else {
          setWorking(false);
          setError("Poynt did not return a payment nonce.");
        }
      });
      instance.on("error", () => {
        setWorking(false);
        setError("Poynt could not tokenize the card.");
      });
      instance.mount("poynt-collect", document, {
        displayComponents: { labels: true },
        inlineErrors: true,
        paymentMethods: ["card"],
      });
      collect.current = instance;
    };
    let script = document.querySelector(`script[src="${details.client_config.collect_script_url}"]`);
    if (script) initialize();
    else {
      script = document.createElement("script");
      script.src = details.client_config.collect_script_url;
      script.async = true;
      script.dataset.poyntCollect = "true";
      script.addEventListener("load", initialize, { once: true });
      script.addEventListener("error", () => setError("The secure Poynt card form could not be loaded."), { once: true });
      document.body.appendChild(script);
    }
    return () => {
      active = false;
      if (collect.current?.unmount) collect.current.unmount("poynt-collect", document);
      collect.current = null;
    };
  }, [details]); // eslint-disable-line react-hooks/exhaustive-deps

  const startPoynt = () => {
    if (!collect.current) {
      setError("The secure Poynt card form could not be loaded.");
      return;
    }
    setWorking(true);
    setError("");
    collect.current.getNonce();
  };

  if (error && !details) return <main className="payments-page"><h1>Payment unavailable</h1><p>{error}</p><button className="back-btn" onClick={() => navigate(`/invoice/${guid}`)}>Back to invoice</button></main>;
  if (!details) return <main className="payments-page">Loading secure payment details...</main>;
  return <main className="payments-page"><h1>Pay with {PROVIDER_NAMES[details.processor]}</h1><p><strong>Amount due:</strong> ${details.amount} USD</p>
    {details.sandbox_mode && <div className="sandbox-banner"><strong>Sandbox mode:</strong> this is a test transaction and will not settle the invoice.{details.processor === "stripe" && <p>Stripe success card: <code>4242 4242 4242 4242</code>; decline: <code>4000 0000 0000 0002</code>; 3DS: <code>4000 0025 0000 3155</code>.</p>}{details.processor === "paypal" && <p>Sign in with a PayPal sandbox buyer account.</p>}{details.processor === "poynt" && <p>Use the test cards assigned to your Poynt test merchant.</p>}</div>}
    {error && <p role="alert">{error}</p>}{details.processor === "poynt" && <div id="poynt-collect" />}<button className="pay-btn" disabled={working} onClick={details.processor === "poynt" ? startPoynt : () => start()}>{working ? "Starting..." : `Pay with ${PROVIDER_NAMES[details.processor]}`}</button><button className="back-btn" onClick={() => navigate(`/invoice/${guid}`)}>Back to invoice</button></main>;
}
